/**
 * Pure helpers for the guest manage page `/book/manage/:token` (#791).
 *
 * The page is SERVER-DRIVEN (E32): it renders from `view.state` and
 * `view.can_cancel`, never from the guest's clock, so a guest whose computer is
 * wrong sees the same page as everyone else. The one client-only fact is
 * "this tab's POST just cancelled it". Guest copy is fixed per status and no
 * server `detail` is ever rendered, which also keeps an object-shaped `detail`
 * from reaching React as a child (#603).
 */
import { browserTimeZone, formatInZone, isValidTimeZone, zoneLabelAt } from './timezone';

export const REASON_MAX = 500;

export const DEFAULT_HOST_NAME = 'Your host';
export const DEFAULT_MEETING_NAME = 'Your meeting';

export const GENERIC_CANCEL_ERROR = "We couldn't cancel your booking. Please try again.";
export const THROTTLED_COPY = 'Too many attempts. Please wait a minute and try again.';
export const REASON_TOO_LONG_COPY = `Please keep the reason to ${REASON_MAX} characters or fewer.`;

/**
 * An `href` the page may render from server data: an absolute http(s) URL or a
 * root-relative path. Anything else (javascript:, data:, protocol-relative) is
 * dropped, so a bad value shows no link instead of a dangerous one.
 */
export function safeHttpUrl(value) {
    if (typeof value !== 'string') return '';
    const url = value.trim();
    if (/^https?:\/\/\S+$/i.test(url)) return url;
    if (/^\/(?!\/)\S*$/.test(url)) return url;
    return '';
}

/**
 * The UX state of a loaded view:
 *   upcoming | cancelled_now | cancelled_guest | cancelled_host | started | ended
 *
 * - `cancelled_now`: this tab's POST answered `result: "cancelled"`
 *   (`justCancelled`). It is the only state that says "We've let … know".
 * - `cancelled_guest`: cancelled earlier by the guest (or `already_cancelled`,
 *   or a legacy row with no `cancelled_by`), with no claim about the host.
 * - `cancelled_host`: `cancelled_by: "coach"`. A host's cancel stays a host's
 *   even when this tab's POST lost the race (the result was `already_cancelled`).
 *
 * Takes no clock. A view with no `state` falls back to `status`.
 */
export function manageState(view, { justCancelled = false } = {}) {
    const v = view || {};
    const state = v.state || (v.status === 'cancelled' ? 'cancelled' : 'upcoming');
    if (state === 'cancelled') {
        if (v.cancelled_by === 'coach') return 'cancelled_host';
        if (justCancelled) return 'cancelled_now';
        return 'cancelled_guest';
    }
    if (state === 'started') return 'started';
    if (state === 'ended') return 'ended';
    return 'upcoming';
}

/** Can the guest cancel from this view? Only an upcoming booking the server allows. */
export function canCancel(view) {
    return manageState(view) === 'upcoming' && view?.can_cancel !== false;
}

/**
 * The page's state for a failed GET: `invalid` (404, or a link the server
 * rejects outright), `throttled` (429, its own screen) or `failed` (network,
 * 5xx). The server's text is never shown.
 */
export function loadErrorState(error) {
    const status = error?.response?.status;
    if (status === 404 || status === 400 || status === 410) return 'invalid';
    if (status === 429) return 'throttled';
    return 'failed';
}

/**
 * What a failed cancel POST does to the page. Returns one of:
 *   { state: 'started', view? }  409 booking_started: render the started state
 *                                from `detail.view` with no refetch
 *   { state: 'invalid' }         404: the booking is gone
 *   { message }                  anything else: fixed copy for the inline alert
 *
 * Never returns a server string: the message is chosen here, per status.
 */
export function cancelErrorCopy(error) {
    const status = error?.response?.status;
    const detail = error?.response?.data?.detail;
    const code = typeof detail === 'string' ? detail : detail?.code;

    if (status === 409 && code === 'booking_started') {
        const view = detail && typeof detail === 'object' && detail.view && typeof detail.view === 'object'
            ? detail.view : undefined;
        return view ? { state: 'started', view } : { state: 'started' };
    }
    if (status === 404) return { state: 'invalid' };
    if (status === 429) return { message: THROTTLED_COPY };
    if (status === 422 && code === 'reason_too_long') return { message: REASON_TOO_LONG_COPY };
    return { message: GENERIC_CANCEL_ERROR };
}

/**
 * Turn the cancel POST's 200 body into `{ view, justCancelled }`, or null when
 * the body is not one the page understands (the caller then shows the generic
 * error rather than claiming a cancel that may not have happened).
 *
 * `result: "cancelled"` means this POST did it; `already_cancelled` means a
 * second tab or the host got there first. A `cancelled` answer whose view still
 * reads otherwise is normalised, so the guest is never left with no feedback.
 */
export function settleCancelResponse(data, previousView) {
    const result = data?.result;
    if (result !== 'cancelled' && result !== 'already_cancelled') return null;
    const base = data?.view && typeof data.view === 'object' ? data.view : previousView || {};
    if (base.state === 'cancelled') return { view: base, justCancelled: result === 'cancelled' };
    return {
        view: {
            ...base,
            state: 'cancelled',
            status: 'cancelled',
            can_cancel: false,
            meet_link: null,
            cancelled_by: base.cancelled_by || 'guest',
        },
        justCancelled: result === 'cancelled',
    };
}

/** The zone the view's times are shown in: the guest's, else the browser's (and labelled). */
export function viewZone(view) {
    return isValidTimeZone(view?.guest_timezone) ? view.guest_timezone.trim() : browserTimeZone();
}

const hasNumber = (n) => typeof n === 'number' && Number.isFinite(n) && n > 0;

function durationMinutes(view) {
    if (hasNumber(Number(view?.duration))) return Math.round(Number(view.duration));
    const a = Date.parse(view?.start_utc);
    const b = Date.parse(view?.end_utc);
    if (Number.isFinite(a) && Number.isFinite(b) && b > a) return Math.round((b - a) / 60000);
    return null;
}

/**
 * The details card's time lines, in the guest's zone:
 *   { date: "Tuesday, October 28, 2026",
 *     time: "3:00 – 3:30 PM · 30 min",
 *     zone: "(GMT+8) Manila (PHT)" }
 * The zone label is `zoneLabelAt` at the MEETING's instant, so a meeting after
 * a DST change reads the offset that applies on its date (E31). Each field is
 * '' when its input is missing.
 */
export function whenLines(view) {
    const v = view || {};
    const zone = viewZone(v);
    const date = formatInZone(v.start_utc, { zone, formatStr: 'EEEE, MMMM d, yyyy' });
    const startClock = formatInZone(v.start_utc, { zone, formatStr: 'h:mm a' });
    const endClock = formatInZone(v.end_utc, { zone, formatStr: 'h:mm a' });
    let range = startClock;
    if (startClock && endClock) {
        const sameHalf = startClock.slice(-2) === endClock.slice(-2);
        range = `${sameHalf ? startClock.slice(0, -3) : startClock} – ${endClock}`;
    }
    const minutes = durationMinutes(v);
    const time = [range, minutes ? `${minutes} min` : ''].filter(Boolean).join(' · ');
    return { date, time, zone: zoneLabelAt(zone, v.start_utc) };
}

/**
 * One line naming the meeting's time for the cancelled screens:
 * "Tuesday, October 28 · 3:00 PM (GMT+8) Manila (PHT)".
 */
export function whenSummary(view) {
    const v = view || {};
    const zone = viewZone(v);
    const day = formatInZone(v.start_utc, { zone, formatStr: 'EEEE, MMMM d' });
    const clock = formatInZone(v.start_utc, { zone, formatStr: 'h:mm a' });
    const label = zoneLabelAt(zone, v.start_utc);
    return [day, [clock, label].filter(Boolean).join(' ')].filter(Boolean).join(' · ');
}

/** The host's full name, or the fallback. */
export function hostName(view) {
    const name = view?.host?.display_name;
    return typeof name === 'string' && name.trim() ? name.trim() : DEFAULT_HOST_NAME;
}

/** The host's name inside a sentence ("with Maria Santos", "with your host"). */
export function hostNameInline(view) {
    const name = view?.host?.display_name;
    return typeof name === 'string' && name.trim() ? name.trim() : DEFAULT_HOST_NAME.toLowerCase();
}

/** The host's first name for "Maria will be told", or '' when the view has none. */
export function hostFirstName(view) {
    const first = view?.host?.first_name;
    return typeof first === 'string' ? first.trim() : '';
}

/** The meeting's name (the link's name), or the fallback. A deleted link has none. */
export function meetingName(view) {
    const name = view?.link?.name;
    return typeof name === 'string' && name.trim() ? name.trim() : DEFAULT_MEETING_NAME;
}

/**
 * The location row (E33). A static link reads "Online meeting": the pasted
 * link can be any provider, so the link's provider name would be a guess. A
 * conference location uses its own label; a booking with a join link and no
 * label reads "Online meeting"; anything else shows no row ('').
 */
export function locationText(view, labelFor) {
    const v = view || {};
    if (v.meeting_link_source === 'static') return 'Online meeting';
    const label = typeof labelFor === 'function' ? labelFor(v.location) : '';
    if (label && label !== v.location) return label;
    return v.meet_link ? 'Online meeting' : '';
}
